<template>
  <div class="column">
    <div class="row q-my-md justify-between">
      <div class="row q-gutter-sm items-center">
        <q-btn
          v-for="priceCard in $_.filter(priceCards.data, priceCard => !priceCard.isArchived)"
          :key="priceCard.id"
          size="sm"
          outline
          :label="priceCard.name"
          :class="{ 'selected-button': paintValue === priceCard.id }"
          @click="paintValue = priceCard.id"
        />
        <q-btn
          size="sm"
          outline
          label="Clear"
          :class="{ 'selected-button': paintValue === null }"
          @click="paintValue = null"
        />
      </div>
    </div>
    <div>Click and draw to change schedule. Click heading to toggle rows and columns.</div>
    <q-markup-table
      separator="cell"
      flat
      bordered
      class="non-selectable"
    >
      <thead>
        <tr>
          <th
            :style="{ 'cursor': editable ? 'se-resize' : '' }"
            @mousedown="editable && toggle()"
            @mouseover="(event) => editable && toggle(null, null, event)"
            @mouseout="previewingAction = false"
          >
            Time Slot
          </th>
          <th
            v-for="dayNumber in currentCircuit.daysOfWeekNumbers"
            :key="dayNumber"
            :style="{ 'cursor': editable ? 's-resize' : '' }"
            @mousedown="editable && toggle(dayNumber)"
            @mouseover="(event) => editable && toggle(dayNumber, null, event)"
            @mouseout="previewingAction = false"
          >
            {{ currentCircuit.daysOfWeek[currentCircuit.daysOfWeekNumbers.indexOf(dayNumber)] }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="timeSlot in timeSlots.data"
          :key="timeSlot.id"
          class="q-tr--no-hover"
        >
          <td
            style="width: 20%"
            :style="{ 'cursor': editable ? 'e-resize' : '' }"
            @mousedown="editable && toggle(null, timeSlot.id)"
            @mouseover="(event) => editable && toggle(null, timeSlot.id, event)"
            @mouseout="previewingAction = false"
          >
            {{ timeSlot.name }}
          </td>
          <td
            v-for="dayNumber in currentCircuit.daysOfWeekNumbers"
            :key="dayNumber"
            :set-value="defaultPriceCard(dayNumber, timeSlot.id)"
            style="width: 10%"
            :style="{ 'cursor': editable ? 'cell' : '' }"
            class="time-slot text-center"
            :class="{ 'selected': !!defaultPriceCard(dayNumber, timeSlot.id), 'preview': isPreview(dayNumber, timeSlot.id) }"
            @mousedown="editable && toggle(dayNumber, timeSlot.id)"
            @mouseover="(event) => editable && toggle(dayNumber, timeSlot.id, event)"
            @mouseout="previewingAction = false"
          >
            <q-chip
              v-if="defaultPriceCard(dayNumber, timeSlot.id)"
              style="pointer-events: none;"
              class="ellipsis"
              square
            >
              {{ defaultPriceCard(dayNumber, timeSlot.id).name }}
            </q-chip>
          </td>
        </tr>
      </tbody>
    </q-markup-table>
  </div>
</template>

<script>

import { timeSlotKey } from 'src/models/circuit/Circuit'

export default {
  name: 'DefaultPriceCardsTable',
  props: {
    defaultPriceCards: {
      type: Object,
      required: true
    }
  },
  emits: ['change'],
  data: function () {
    return {
      paintValue: null,
      editable: true,
      previewingAction: false,
      defaultPriceCardsPreview: {},
      saving: false,
      priceCardCache: null
    }
  },
  configuration: ['currentCircuit', 'priceCards', 'timeSlots'],
  methods: {
    getPriceCard (id) {
      if (!this.priceCardCache) {
        this.priceCardCache = {}
        _.each(this.priceCards.data, priceCard => { this.priceCardCache[priceCard.id] = priceCard })
      }

      return this.priceCardCache[id]
    },
    isPreview (dayNumber, timeSlotId) {
      return this.previewingAction && this.defaultPriceCardsPreview[timeSlotKey(dayNumber, timeSlotId)] !== this.defaultPriceCards[timeSlotKey(dayNumber, timeSlotId)]
    },
    defaultPriceCard (dayNumber, timeSlotId) {
      if (this.previewingAction) {
        return this.getPriceCard(this.defaultPriceCardsPreview[timeSlotKey(dayNumber, timeSlotId)])
      } else {
        return this.getPriceCard(this.defaultPriceCards[timeSlotKey(dayNumber, timeSlotId)])
      }
    },
    toggle (dayNumberFilter, timeSlotIdFilter, event) {
      // Preview or actual
      let defaultPriceCards

      const dragging = event && event.buttons
      this.previewingAction = event && !event.buttons

      if (this.previewingAction) {
        // Clone only the hashes and not the models
        defaultPriceCards = _.clone(this.defaultPriceCards)
      } else {
        defaultPriceCards = this.defaultPriceCards
      }

      if (!dragging) {
        this.toggleValue = undefined
      }

      _.each(this.timeSlots.data, (timeSlot) => {
        if (timeSlotIdFilter && timeSlot.id !== timeSlotIdFilter) {
          return
        }
        _.each(this.currentCircuit.daysOfWeekNumbers, (dayNumber) => {
          if (dayNumberFilter !== null && dayNumberFilter !== undefined && dayNumber !== dayNumberFilter) {
            return
          }

          const key = timeSlotKey(dayNumber, timeSlot.id)
          const customValue = this.defaultPriceCards[key]

          // Whats the value
          let finalValue

          if (this.toggleValue !== undefined) {
            finalValue = this.toggleValue
          } else {
            // We are setting the toggle value for this and any upcoming drag events
            this.toggleValue = this.paintValue

            // If the paintValue is already what we are attempting to paint, toggle to unselected
            if (customValue && customValue === this.paintValue) {
              this.toggleValue = null
            }

            finalValue = this.toggleValue
          }

          if (!this.previewingAction) {
            if (!finalValue) {
              delete defaultPriceCards[key]
              return
            }
            if (finalValue === customValue) {
              return
            }

            defaultPriceCards[key] = finalValue
          } else {
            // Do things a little different if previewing
            if (finalValue === customValue) {
              return
            }

            if (!finalValue && !customValue) {
              delete defaultPriceCards[key]
              return
            }

            // If we are previewing adding or removing, we want to show a value with an indicator that it's a preview
            // e.g. if we are deleting something, we want to show the old value, so not always the preview value
            if (finalValue !== customValue) {
              finalValue = (finalValue || customValue)
            }

            defaultPriceCards[key] = finalValue
          }
        })
      })

      // Trigger change
      if (this.previewingAction) {
        this.defaultPriceCardsPreview = null
        this.defaultPriceCardsPreview = defaultPriceCards
      } else {
        this.$emit('change', null)
        this.$emit('change', defaultPriceCards)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .selected-button {
    background-color: #DDD !important;
  }

  .time-slot {
    background-color: #DAA;
  }

  .time-slot.selected {
    background-color: #E9E9E9;
  }

  .time-slot.preview {
    background-color: #CCC;
  }

  .non-selectable table {
    table-layout: fixed;
  }
</style>

<style lang="scss">
  .non-selectable table {
    table-layout: fixed;
  }
</style>
